/**
 * JoinVerifyContainer — SMS verify screen for the group invite flow
 * Route: /groups/join/:gid/verify?inviter=<uid>
 *
 * Real Firebase phone auth (confirms the OTP). On success, records the pending
 * join on the user's own doc — the APP completes the join on next login.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';
import useJoinPageChrome from './useJoinPageChrome';
import {
  verifyCode,
  resendCode,
  createRecaptchaVerifier,
  getPendingPhone,
  getPendingDisplayPhone,
  hasPendingVerification,
  clearPending,
} from './firebaseAuth';
import { writePendingGroupJoin } from '../../API/groups';
import { getProfileName } from '../../API/users';
import { withTimeout, readInviteParams, buildInviteQuery } from './inviteHelpers';
import { track, identify, trackAccountCreated, EVENTS, SURFACE, LINK_SOURCE } from '../../analytics/track';
import { normalizeLinkSource } from '../../analytics/helpers';
import JoinFooter from './JoinFooter';
import useKeyboardPinnedFooter from './useKeyboardPinnedFooter';

// ─── Subcomponents ────────────────────────────────────────────────────────────

function Wordmark({ size = 22, color = brand.caviar }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
      }}
    >
      <svg width={size * 0.95} height={size * 0.95} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 4h8a4 4 0 010 8H10a4 4 0 010 8h-4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      splyt
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    // svh, matching the wrapper below. `100vh` is the LARGE viewport on iOS
    // Safari (toolbars hidden), so it made the document ~80 to 100px taller
    // than the visible area and the page scrolled for no reason.
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  // Mobile layout
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    // border-box so the padding below stays INSIDE the viewport-sized column;
    // with the default content-box the column is taller than the screen and the
    // bottom-aligned footer falls under the browser toolbar.
    boxSizing: 'border-box',
    minHeight: '100vh',
    // svh, not dvh: iOS Chrome treats dvh as the bars-hidden height and lays
    // the footer out behind the bottom toolbar (same rationale as the name and
    // handoff screens).
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    padding: `${spacing.md}px ${spacing.md + 4}px 0`,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  // Desktop layout
  desktopWrapper: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '24px 40px 40px',
    },
  },

  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  desktopCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingBottom: spacing.md,
  },

  desktopContent: {
    display: 'flex',
    flexDirection: 'column',
  },

  // Title
  title: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: brand.caviar,
  },

  titleMobile: {
    fontSize: 26,
    lineHeight: 1.1,
  },

  titleDesktop: {
    fontSize: 30,
    lineHeight: 1.1,
  },

  subtitle: {
    margin: '6px 0 28px',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  phoneNumber: {
    fontWeight: 600,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
  },

  // Code boxes container
  codeBoxesContainer: {
    display: 'flex',
    gap: 10,
    position: 'relative',
  },

  codeBoxesShake: {
    animation: '$shake 200ms ease-in-out',
  },

  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '12.5%': { transform: 'translateX(-6px)' },
    '37.5%': { transform: 'translateX(6px)' },
    '62.5%': { transform: 'translateX(-6px)' },
    '87.5%': { transform: 'translateX(6px)' },
  },

  // Individual code box (visual only)
  codeBox: {
    flex: 1,
    aspectRatio: '1 / 1.15',
    backgroundColor: brand.surface,
    borderRadius: radii.input,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    cursor: 'text',
  },

  codeBoxEmpty: {
    border: `1.5px solid ${brand.marshNeutral}`,
  },

  codeBoxFilled: {
    border: `1.5px solid ${brand.caviar25}`,
  },

  codeBoxActive: {
    border: `1.5px solid ${brand.caviar}`,
    boxShadow: shadows.inputFocus,
  },

  // Hidden input that captures all keystrokes
  hiddenInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'text',
    fontSize: 16, // Prevents zoom on iOS
  },

  // Cursor blink
  cursor: {
    width: 2,
    height: 28,
    backgroundColor: brand.caviar,
    animation: '$blink 1s steps(2, end) infinite',
  },

  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },

  // Empty placeholder dash
  emptyPlaceholder: {
    width: 14,
    height: 2,
    backgroundColor: brand.caviar25,
    borderRadius: 1,
  },

  // Error message
  errorMessage: {
    marginTop: 16,
    padding: '12px 14px',
    backgroundColor: `${brand.ketchup}15`,
    borderRadius: radii.lg,
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.4,
    color: brand.ketchup,
    textAlign: 'center',
  },

  // Meta row (timer + wrong number)
  metaRow: {
    marginTop: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  timerText: {
    fontWeight: 400,
    fontSize: 13,
    lineHeight: 1,
    color: brand.caviar50,
  },

  timerCount: {
    fontWeight: 600,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
  },

  resendLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },

  wrongNumberLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },

  // Footer
  footer: {
    flexShrink: 0,
    padding: `${spacing.sm}px 0`,
    paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
  },

  desktopFooter: {
    marginTop: spacing.lg,
  },

  // Primary button
  primaryButton: {
    width: '100%',
    backgroundColor: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.1,
    letterSpacing: '-0.005em',
    boxShadow: shadows.primaryButton,
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 150ms ease',
    '&:hover:not(:disabled)': {
      transform: 'scale(0.985)',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: brand.caviar25,
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  },

  // Header link
  headerLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },

  // Invisible reCAPTCHA container for resend
  recaptchaContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JoinVerifyContainer() {
  useJoinPageChrome();
  const classes = useStyles();
  const { gid } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { inviter, inviteCode, utmSource } = readInviteParams(location.search);
  const query = buildInviteQuery({ inviter, inviteCode, utmSource });
  // Acquisition source for a brand new account: the link's utm_source when it
  // has one, else the texted invite (an i= code) or a plain link (direct).
  const signupSource = normalizeLinkSource(
    utmSource,
    inviteCode ? LINK_SOURCE.GROUP_INVITE_TEXT : LINK_SOURCE.DIRECT,
  );

  // Get phone from module state (set by JoinPhoneContainer)
  const phone = getPendingPhone();
  const displayPhone = getPendingDisplayPhone();

  // Single code string state (simpler than array)
  const [code, setCode] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Resend timer state
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Single input ref
  const inputRef = useRef(null);
  // Lifts the CTA above the software keyboard on iOS (see the hook's header).
  // The sixth digit auto-submits (handleInputChange below), so on mobile this
  // bar is mostly the "Verifying..." progress state — but Luis asked for the
  // button to clear the keypad on this screen too, so it stays and rides up.
  const { footerRef, footerHeight, pinned } = useKeyboardPinnedFooter();

  // RecaptchaVerifier for resend functionality - matches SignUpModal pattern
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  // Redirect to phone screen if no pending verification (user landed directly via URL)
  useEffect(() => {
    if (!hasPendingVerification()) {
      history.replace(`/groups/join/${gid}/phone${query}`);
    }
  }, [history, gid, query]);

  // Initialize RecaptchaVerifier on mount for resend
  useEffect(() => {
    setRecaptchaVerifier(createRecaptchaVerifier('join-verify-recaptcha-container'));
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Submit verification
  const submitVerification = useCallback(
    async (codeToVerify) => {
      if (isLoading || !hasPendingVerification()) return;

      setIsLoading(true);
      setError('');

      try {
        // verifyCode reads confirmation from module state; returns the signed-in user.
        const { user, isNewUser } = await verifyCode(codeToVerify);

        // Phone auth is never anonymous: identify before anything else so the
        // events below land on the real user even if the write fails.
        identify(user);

        // Record the pending join on the user's OWN doc. The APP completes the
        // actual join on next login (it has App Check). A write failure must NOT
        // block the user — the warm re-tap path still works — so swallow + log.
        try {
          if (user && user.uid) {
            await writePendingGroupJoin(user.uid, gid, inviter, inviteCode);
          }
        } catch (writeErr) {
          console.error('[JoinVerify] pending-join write failed:', writeErr);
        }

        track(EVENTS.SIGNED_IN, {
          surface: SURFACE.GROUP,
          is_new_user: isNewUser,
          group_id: gid,
          invite_id: inviteCode,
        });
        if (isNewUser) {
          trackAccountCreated({
            surface: SURFACE.GROUP,
            referrer_user_id: inviter,
            group_id: gid,
            invite_id: inviteCode,
            source: signupSource,
          });
        }

        // Clear pending state before navigating
        clearPending();

        // Does this user already have a profile name? Decide BEFORE navigating,
        // still under the "Verifying..." state, so users with a saved name never
        // see the name screen. Nameless web joiners otherwise show up to every
        // other group member as "Member" forever.
        let needsName = false;
        if (user && user.uid) {
          try {
            // 4s budget: a stalled read must not hold "Verifying..." forever.
            // Timing out reads as nameless; the name screen is the safe side.
            needsName = !(await withTimeout(getProfileName(user.uid), 4000, ''));
          } catch (readErr) {
            // Can't tell: send them to the name screen, which re-checks behind
            // a spinner and forwards users who already have a name.
            console.error('[JoinVerify] name check failed:', readErr);
            needsName = true;
          }
        }

        // Success — collect a name if needed, then the download hand-off.
        history.push(
          needsName
            ? `/groups/join/${gid}/name${query}`
            : `/groups/join/${gid}/app${query}`
        );
      } catch (err) {
        console.error('Verification failed:', err);

        // Shake and show error
        setIsShaking(true);
        setError(err.message || 'Verification failed. Please try again.');

        // Reset after shake animation
        setTimeout(() => {
          setIsShaking(false);
          setCode('');
          inputRef.current?.focus();
        }, 200);

        setIsLoading(false);
      }
    },
    [history, gid, inviter, inviteCode, query, isLoading, signupSource]
  );

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);

    // Clear error on input
    if (error) setError('');

    // Auto-submit when 6 digits entered
    if (value.length === 6) {
      submitVerification(value);
    }
  };

  // Handle keydown for backspace behavior
  const handleKeyDown = (e) => {
    // Allow normal behavior for most keys
    if (e.key === 'Backspace' && code.length === 0) {
      // Already empty, nothing to do
      return;
    }
  };

  // Handle resend
  const handleResend = async () => {
    if (!canResend || !hasPendingVerification() || isLoading) return;

    setCanResend(false);
    setTimeLeft(60);
    setError('');
    setCode('');
    setIsLoading(true);

    try {
      // resendCode handles reCAPTCHA reset and updates module state
      await resendCode(recaptchaVerifier);
    } catch (err) {
      console.error('Resend failed:', err);
      setError(err.message || 'Failed to resend code. Please try again.');
      setCanResend(true); // Allow retry on error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle wrong number
  const handleWrongNumber = () => {
    clearPending(); // Clear pending state when going back
    history.push(`/groups/join/${gid}/phone${query}`);
  };

  // Handle manual verify button click
  const handleVerifyClick = () => {
    if (code.length === 6 && !isLoading) {
      submitVerification(code);
    }
  };

  // Focus the hidden input when clicking anywhere on the boxes
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const isCodeComplete = code.length === 6;

  // Convert code string to array for display
  const digits = code.split('').concat(Array(6 - code.length).fill(''));
  const activeIndex = Math.min(code.length, 5);

  // Focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Render code boxes
  const renderCodeBoxes = () => (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={`${classes.codeBoxesContainer} ${
        isShaking ? classes.codeBoxesShake : ''
      }`}
      onClick={handleContainerClick}
      onKeyDown={() => inputRef.current?.focus()}
      role="group"
      aria-label="Verification code input"
    >
      {digits.map((digit, index) => {
        const isFilled = digit !== '';
        const isActive = isFocused && index === activeIndex && !isLoading;

        let boxClass = classes.codeBox;
        if (isActive) {
          boxClass += ` ${classes.codeBoxActive}`;
        } else if (isFilled) {
          boxClass += ` ${classes.codeBoxFilled}`;
        } else {
          boxClass += ` ${classes.codeBoxEmpty}`;
        }

        return (
          <div key={index} className={boxClass}>
            {digit || (isActive ? (
              <div className={classes.cursor} />
            ) : (
              <div className={classes.emptyPlaceholder} />
            ))}
          </div>
        );
      })}
      {/* Single hidden input that captures all keystrokes */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className={classes.hiddenInput}
        value={code}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={isLoading}
        aria-label="6-digit verification code"
      />
    </div>
  );

  // Shared content renderer
  const renderContent = (isMobile) => (
    <>
      <h2
        className={`${classes.title} ${
          isMobile ? classes.titleMobile : classes.titleDesktop
        }`}
      >
        Check your texts
      </h2>

      <p className={classes.subtitle}>
        We sent a 6-digit code to{' '}
        <span className={classes.phoneNumber}>
          {displayPhone || phone || '(---) ---‑----'}
        </span>
        .
      </p>

      {renderCodeBoxes()}

      {error && <div className={classes.errorMessage}>{error}</div>}

      <div className={classes.metaRow}>
        {canResend ? (
          <button
            type="button"
            className={classes.resendLink}
            onClick={handleResend}
          >
            Send a new code
          </button>
        ) : (
          <span className={classes.timerText}>
            Resend code in{' '}
            <span className={classes.timerCount}>{formatTime(timeLeft)}</span>
          </span>
        )}
        <button
          type="button"
          className={classes.wrongNumberLink}
          onClick={handleWrongNumber}
        >
          Wrong number?
        </button>
      </div>
    </>
  );

  // Footer content
  const renderFooter = () => (
    <button
      type="button"
      className={classes.primaryButton}
      disabled={!isCodeComplete || isLoading}
      onClick={handleVerifyClick}
      // iOS fires blur before click: without this the first tap on the pinned
      // bar only closes the keyboard and the button slides out from under the finger.
      onMouseDown={(e) => e.preventDefault()}
    >
      {isLoading ? 'Verifying...' : 'Verify'}
    </button>
  );

  return (
    <div className={classes.root}>
      {/* Mobile layout */}
      <div className={classes.mobileWrapper}>
        <div className={classes.content} style={{ paddingBottom: footerHeight }}>
          {renderContent(true)}
        </div>
        <JoinFooter footerRef={footerRef} pinned={pinned}>
          {renderFooter()}
        </JoinFooter>
      </div>

      {/* Desktop layout */}
      <div className={classes.desktopWrapper}>
        <div className={classes.desktopHeader}>
          <Wordmark size={22} />
          <button type="button" className={classes.headerLink}>
            Already have Splyt?
          </button>
        </div>
        <div className={classes.desktopCard}>
          <div className={classes.desktopContent}>{renderContent(false)}</div>
          <div className={classes.desktopFooter}>{renderFooter()}</div>
        </div>
      </div>

      {/* reCAPTCHA container - required for resend functionality */}
      <span id="join-verify-recaptcha-container" />
    </div>
  );
}
